// lib/searchEntities.tsx
'use client';

import { Entity, BoundsSearchRequest, BoundsSearchResponse, Bounds } from '../types';

export interface RouteOption {
  route_id: number;
  name: string;
  description: string;
  distance: string;
  duration: string;
  type: 'highway' | 'coastal' | 'scenic' | 'standard';
}

export interface RouteOptionsResponse {
  route_options: RouteOption[];
  fallback?: boolean;
}

// Update EnhancedSearchParams interface to include route selection
export interface EnhancedSearchParams extends SearchParams {
  search_type?: 'location' | 'route' | 'similar';
  entity_id?: number; // for similarity search
  max_deviation?: number; // for route search
  region_search?: boolean; // search within region hierarchy
  include_nearby?: boolean; // include graph-connected nearby locations
  
  // New route selection params
  get_route_options?: boolean; // Request route options instead of search
  selected_route?: number; // Which route to use (0, 1, 2, etc.)
  corridor_width?: number; // Width of search corridor along route
  viaPoints?: string[];
}

// New function to search with route selection workflow
export const searchWithRouteSelection = async (
  startPoint: string,
  endPoint: string,
  features?: string[],
  maxDeviation: number = 50,
  onRouteOptions?: (options: RouteOption[]) => Promise<number>
): Promise<EnhancedSearchResponse> => {
  // Step 1: Get route options
  const routeOptions = await getRouteOptions(startPoint, endPoint);
  
  let selectedRoute = 0; // Default to first route
  
  // Step 2: If multiple routes and callback provided, let user select
  if (routeOptions.route_options.length > 1 && onRouteOptions) {
    selectedRoute = await onRouteOptions(routeOptions.route_options);
  }
  
  // Step 3: Search along selected route
  return searchAlongRoute(startPoint, endPoint, features, maxDeviation, selectedRoute);
};

  export interface EnhancedSearchResponse extends SearchResponse {
    entities: EnhancedEntity[];
    search_metadata?: {
      search_type: string;
      execution_time?: number;
      cached?: boolean;
      api_used?: string;
      route_info?: {
        start: string;
        end: string;
        total_distance?: number;
        route_name?: string;
        route_via?: string;
        route_distance?: string;
        route_duration?: string;
        route_type?: string;
        search_method?: string;
        corridor_width_km?: number;
        start_coords?: { lat: number; lon: number };
        end_coords?: { lat: number; lon: number };
        route_distance_km?: number;
        max_deviation?: number;
        buffer_degrees?: number;
      };
    };
  }
  
  export interface SearchParams {
    location?: string;
    radius: number;
    features?: string[];
    type?: string;
    page?: number;
    per_page?: number;
    isRouteSearch?: boolean;
    startPoint?: string;
    endPoint?: string;
    sort?: string;
    selectedRoute?: number;
  }

// Extended search params for enhanced Neo4j features
export interface EnhancedSearchParams extends SearchParams {
  search_type?: 'location' | 'route' | 'similar';
  entity_id?: number; // for similarity search
  max_deviation?: number; // for route search
  region_search?: boolean; // search within region hierarchy
  include_nearby?: boolean; // include graph-connected nearby locations
  viaPoints?: string[];
}

export interface SearchResponse {
  entities: Entity[];
  total: number;
  pages: number;
  current_page: number;
}

// Enhanced entity type for search results
export interface EnhancedEntity extends Entity {
  distance_km?: number;
  similarity_score?: number;
  common_features?: string[];
  deviation_km?: number; // for route search
  distance_from_route?: number; // distance from the route line (in km)
  match_reason?: string; // explanation of why this result matched
  coordinates?: {
    lat: number;
    lon: number;
  };
}

// Location suggestion type for enhanced autocomplete
export interface LocationSuggestion {
  value: string;
  type: 'region' | 'location' | 'campsite' | 'route';
  popularity?: number;
  metadata?: {
    state?: string;
    region?: string;
    entity_count?: number;
  };
}

export const isRouteOptionsResponse = (
  response: EnhancedSearchResponse | RouteOptionsResponse
): response is RouteOptionsResponse => {
  return 'route_options' in response;
};

export const getRouteOptions = async (
  startPoint: string,
  endPoint: string
): Promise<RouteOptionsResponse> => {
  console.log('Getting route options:', { startPoint, endPoint });
  
  try {
    const requestBody = {
      search_type: 'route',
      start_point: startPoint,
      end_point: endPoint,
      get_route_options: true,
      radius: 50 // Required but not used for options
    };
    
    const response = await fetch('https://app.trip-planner.com.au/api/entities/smart-search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data: RouteOptionsResponse = await response.json();
    console.log('Route options received:', data);
    
    return data;
  } catch (error) {
    console.error('Error getting route options:', error);
    // Return fallback single route
    return {
      route_options: [{
        route_id: 0,
        name: 'Direct Route',
        description: `${startPoint} to ${endPoint}`,
        distance: 'Unknown',
        duration: 'Unknown',
        type: 'standard'
      }],
      fallback: true
    };
  }
};

const validateSearchParams = (params: SearchParams | EnhancedSearchParams): string | null => {
  if (!params.location && params.radius <= 0 && !('entity_id' in params)) {
    return 'Either a valid location, positive radius, or entity_id must be provided';
  }
  if (params.radius && (typeof params.radius !== 'number' || params.radius <= 0)) {
    return 'Radius must be a positive number';
  }
  if (params.features && !Array.isArray(params.features)) {
    return 'Features must be an array';
  }
  if (params.type && typeof params.type !== 'string') {
    return 'Type must be a string';
  }
  if (params.page && (typeof params.page !== 'number' || params.page <= 0)) {
    return 'Page must be a positive number';
  }
  if (params.per_page && (typeof params.per_page !== 'number' || params.per_page <= 0)) {
    return 'Per page must be a positive number';
  }
  if (params.sort && typeof params.sort !== 'string') {
    return 'Sort must be a string';
  }
  
  // Validate enhanced params
  const enhanced = params as EnhancedSearchParams;
  if (enhanced.search_type && !['location', 'route', 'similar'].includes(enhanced.search_type)) {
    return 'Invalid search type';
  }
  if (enhanced.search_type === 'similar' && !enhanced.entity_id) {
    return 'Entity ID required for similarity search';
  }
  if (enhanced.search_type === 'route' && (!enhanced.startPoint || !enhanced.endPoint)) {
    return 'Start and end points required for route search';
  }
  
  return null;
};

// Original search function - maintained for backward compatibility
export const searchEntities = async (params: SearchParams): Promise<SearchResponse> => {
  console.log('searchEntities called with params:', JSON.stringify(params, null, 2));

  // Validate params
  const validationError = validateSearchParams(params);
  if (validationError) {
    console.error('Invalid search params:', validationError);
    throw new Error(`Invalid search parameters: ${validationError}`);
  }

  try {
    const url = params.isRouteSearch
      ? 'https://app.trip-planner.com.au/api/entities/route-search'
      : 'https://app.trip-planner.com.au/api/entities/search';

    // Prepare request body with all parameters including sort
    const requestBody = {
      ...params,
      radius: Number(params.radius),
      page: Number(params.page || 1),
      per_page: Number(params.per_page || 20),
      // Include sort parameter if provided
      ...(params.sort && { sort: params.sort })
    };

    console.log('Request body:', JSON.stringify(requestBody, null, 2));

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify(requestBody)
    });

    console.log('Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API responded with status ${response.status}: ${errorText}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: SearchResponse = await response.json();
    
    // Save search params and results to session storage (including sort)
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('lastSearch', JSON.stringify({
        params,
        results: data.entities,
        total: data.total,
        pages: data.pages,
        current_page: data.current_page,
        sort: params.sort || 'featured'
      }));
    }

    console.log('Search completed successfully:', {
      entitiesCount: data.entities.length,
      total: data.total,
      currentPage: data.current_page,
      sort: params.sort || 'featured'
    });

    return data;
  } catch (error) {
    console.error('Error in searchEntities:', error);
    
    if (error instanceof Error) {
      throw new Error(`Failed to fetch entities: ${error.message}`);
    }
    throw new Error('An unknown error occurred while fetching entities');
  }
};




export const searchEntitiesEnhanced = async (
  params: EnhancedSearchParams
): Promise<EnhancedSearchResponse> => {
  console.log('searchEntitiesEnhanced called with params:', JSON.stringify(params, null, 2));

  // Special handling for route options request
  if (params.get_route_options) {
    throw new Error('Use getRouteOptions() function for fetching route options');
  }

  // Validate params
  const validationError = validateSearchParams(params);
  if (validationError) {
    console.error('Invalid search params:', validationError);
    throw new Error(`Invalid search parameters: ${validationError}`);
  }

  try {
    // Determine search type and endpoint
    let url = 'https://app.trip-planner.com.au/api/entities/smart-search';
    
    // For backward compatibility, check if it's a traditional route search
    if (params.isRouteSearch && !params.search_type) {
      params.search_type = 'route';
      if (!params.startPoint && !params.endPoint && params.location) {
        // Parse location for route format "X to Y"
        const routeParts = params.location.match(/(.+?)\s+to\s+(.+)/i);
        if (routeParts) {
          params.startPoint = routeParts[1].trim();
          params.endPoint = routeParts[2].trim();
        }
      }
    }

    // Prepare enhanced request body
    const requestBody = {
      ...params,
      search_type: params.search_type || 'location',
      radius: Number(params.radius),
      page: Number(params.page || 1),
      per_page: Number(params.per_page || 20),
      max_deviation: params.max_deviation || 50,
      ...(params.sort && { sort: params.sort }),
      // Include route selection params (but not get_route_options)
      selected_route: params.selected_route,
      corridor_width: params.corridor_width
    };

    console.log('Enhanced request body:', JSON.stringify(requestBody, null, 2));
    console.warn('NOTE: Enhanced search API may be unreliable - consider using standard search for basic queries');

    const startTime = Date.now();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify(requestBody)
    });

    const executionTime = Date.now() - startTime;
    console.log(`Response status: ${response.status}, execution time: ${executionTime}ms`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Enhanced search API responded with status ${response.status}: ${errorText}`);
      
      // Fallback to original search if enhanced search fails
      if (response.status === 404 || response.status === 500) {
        console.warn('Enhanced search endpoint failed, falling back to standard search');
        const fallbackResult = await searchEntities(params);
        return {
          ...fallbackResult,
          entities: fallbackResult.entities || [],
          search_metadata: {
            search_type: params.search_type || 'location',
            execution_time: executionTime,
            cached: false,
            fallback: true
          }
        } as EnhancedSearchResponse;
      }
      
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    // Ensure it's a search response (not route options)
    const searchData = data as EnhancedSearchResponse;
    
    // Check if the enhanced search returned empty results - this indicates backend issues
    if (searchData.total === 0 && searchData.entities && searchData.entities.length === 0) {
      console.warn('Enhanced search returned 0 results - this may indicate backend API issues');
    }
    
    // Add metadata if not provided by backend
    if (!searchData.search_metadata) {
      searchData.search_metadata = {
        search_type: params.search_type || 'location',
        execution_time: executionTime,
        cached: false,
        api_used: 'enhanced'
      };
    }
    
    // Process entities to ensure enhanced properties are properly typed
    if (searchData.entities) {
      searchData.entities = searchData.entities.map(entity => ({
        ...entity,
        distance_km: entity.distance_km !== undefined ? Number(entity.distance_km) : undefined,
        similarity_score: entity.similarity_score !== undefined ? Number(entity.similarity_score) : undefined,
        deviation_km: entity.deviation_km !== undefined ? Number(entity.deviation_km) : undefined,
        distance_from_route: entity.distance_from_route !== undefined ? Number(entity.distance_from_route) : undefined,
      }));
    }
    
    // Save enhanced search params and results
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('lastEnhancedSearch', JSON.stringify({
        params,
        results: searchData.entities,
        total: searchData.total,
        pages: searchData.pages,
        current_page: searchData.current_page,
        sort: params.sort || 'featured',
        metadata: searchData.search_metadata
      }));
      
      // Also update standard lastSearch for compatibility
      sessionStorage.setItem('lastSearch', JSON.stringify({
        params,
        results: searchData.entities,
        total: searchData.total,
        pages: searchData.pages,
        current_page: searchData.current_page,
        sort: params.sort || 'featured'
      }));
    }

    console.log('Enhanced search completed:', {
      entitiesCount: searchData.entities?.length || 0,
      total: searchData.total,
      currentPage: searchData.current_page,
      sort: params.sort || 'featured',
      searchType: searchData.search_metadata?.search_type,
      executionTime: `${executionTime}ms`,
      warning: searchData.total === 0 ? 'No results returned - possible backend issue' : null
    });

    return searchData;
  } catch (error) {
    console.error('Error in searchEntitiesEnhanced:', error);
    
    if (error instanceof Error) {
      throw new Error(`Enhanced search failed: ${error.message}`);
    }
    throw new Error('An unknown error occurred in enhanced search');
  }
};

export const getEnhancedLocationSuggestions = async (
  query: string
): Promise<LocationSuggestion[]> => {
  if (!query || query.length < 2) {
    return [];
  }

  try {
    // First try enhanced endpoint
    const response = await fetch(
      `https://app.trip-planner.com.au/api/location-suggestions-enhanced?query=${encodeURIComponent(query)}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      }
    );

    if (!response.ok) {
      // Fallback to standard suggestions
      if (response.status === 404) {
        return getStandardLocationSuggestions(query);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const suggestions: LocationSuggestion[] = await response.json();
    
    // Process route-style queries (e.g., "Sydney to Brisbane")
    if (query.includes(' to ')) {
      const routeSuggestion: LocationSuggestion = {
        value: query,
        type: 'route',
        metadata: {
          entity_count: 0
        }
      };
      return [routeSuggestion, ...suggestions];
    }
    
    return suggestions;
  } catch (error) {
    console.error('Error fetching enhanced suggestions:', error);
    // Fallback to standard suggestions
    return getStandardLocationSuggestions(query);
  }
};

// Standard location suggestions (fallback)
const getStandardLocationSuggestions = async (
  query: string
): Promise<LocationSuggestion[]> => {
  try {
    const response = await fetch(
      `https://app.trip-planner.com.au/api/location-suggestions?query=${encodeURIComponent(query)}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const suggestions: string[] = await response.json();
    
    // Convert to LocationSuggestion format
    return suggestions.map(s => ({
      value: s,
      type: 'location' as const,
      popularity: 0
    }));
  } catch (error) {
    console.error('Error fetching standard suggestions:', error);
    return [];
  }
};

// Find similar campsites
export const findSimilarCampsites = async (
  entityId: number,
  limit: number = 5
): Promise<EnhancedSearchResponse> => {
  const params: EnhancedSearchParams = {
    search_type: 'similar',
    entity_id: entityId,
    radius: 50, // Required but not used for similarity search
    page: 1,
    per_page: limit
  };
  
  // This will always return EnhancedSearchResponse now
  return searchEntitiesEnhanced(params);
};
// Search along a route
export const searchAlongRoute = async (
  startPoint: string,
  endPoint: string,
  features?: string[],
  maxDeviation: number = 50,
  selectedRoute?: number
): Promise<EnhancedSearchResponse> => {
  const params: EnhancedSearchParams = {
    search_type: 'route',
    location: `${startPoint} to ${endPoint}`,
    startPoint,
    endPoint,
    radius: maxDeviation,
    max_deviation: maxDeviation,
    features,
    page: 1,
    per_page: 20,
    selected_route: selectedRoute // Add selected route
  };
  
  // This will always return EnhancedSearchResponse now
  return searchEntitiesEnhanced(params);
};

// Export a unified search function that automatically uses the best available method
export const search = async (params: EnhancedSearchParams): Promise<EnhancedSearchResponse> => {
  console.log('Unified search called with params:', params);
  
  // For basic location searches, use standard search API (which works reliably)
  if (params.search_type === 'location' || !params.search_type) {
    console.log('Using standard search API for location search');
    try {
      const result = await searchEntities(params);
      console.log('Standard search completed successfully:', result);
      return {
        ...result,
        entities: result.entities || [],
        search_metadata: {
          search_type: 'location',
          cached: false,
          api_used: 'standard'
        }
      } as EnhancedSearchResponse;
    } catch (error) {
      console.warn('Standard search failed, trying enhanced search as fallback:', error);
      // Continue to enhanced search below
    }
  }
  
  // Use enhanced search for route/similarity searches or as fallback
  try {
    console.log('Using enhanced search API');
    const result = await searchEntitiesEnhanced(params);
    console.log('Enhanced search completed:', result);
    return result;
  } catch (error) {
    console.error('Both search methods failed:', error);
    
    // Final fallback: basic entities with no filtering
    console.warn('Falling back to basic entities endpoint');
    try {
      const basicParams = {
        per_page: params.per_page || 20,
        page: params.page || 1
      };
      
      const response = await fetch('https://app.trip-planner.com.au/api/entities', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      return {
        entities: Array.isArray(data.entities) ? data.entities : [],
        total: data.entities ? data.entities.length : 0,
        pages: 1,
        current_page: 1,
        search_metadata: {
          search_type: 'fallback',
          cached: false,
          api_used: 'basic',
          note: 'Fallback to basic entities due to search API failures'
        }
      } as EnhancedSearchResponse;
    } catch (basicError) {
      console.error('All search methods failed, returning empty results:', basicError);
      
      // Return empty but valid response structure
      return {
        entities: [],
        total: 0,
        pages: 0,
        current_page: 1,
        search_metadata: {
          search_type: 'error',
          cached: false,
          api_used: 'none',
          error: 'All search APIs failed'
        }
      } as EnhancedSearchResponse;
    }
  }
};

// Bounds-based search for map viewport loading
export const searchEntitiesByBounds = async (
  request: BoundsSearchRequest
): Promise<BoundsSearchResponse> => {
  console.log('searchEntitiesByBounds called with request:', JSON.stringify(request, null, 2));

  // Validate bounds
  if (!request.bounds || 
      typeof request.bounds.north !== 'number' ||
      typeof request.bounds.south !== 'number' ||
      typeof request.bounds.east !== 'number' ||
      typeof request.bounds.west !== 'number') {
    throw new Error('Invalid bounds: All bounds properties (north, south, east, west) must be numbers');
  }

  // Validate latitude range
  if (request.bounds.south > request.bounds.north || 
      request.bounds.south < -90 || 
      request.bounds.north > 90) {
    throw new Error('Invalid latitude range: south must be <= north, both between -90 and 90');
  }

  // Validate longitude range (note: can cross 180° meridian)
  if (request.bounds.west < -180 || request.bounds.west > 180 ||
      request.bounds.east < -180 || request.bounds.east > 180) {
    throw new Error('Invalid longitude range: must be between -180 and 180');
  }

  try {
    const url = 'https://app.trip-planner.com.au/api/entities/bounds-search';

    const requestBody = {
      bounds: request.bounds,
      zoom: request.zoom || 10,
      limit: Math.min(request.limit || 200, 500), // Cap at 500 entities
      filters: request.filters || {}
    };

    console.log('Bounds search request body:', JSON.stringify(requestBody, null, 2));

    const startTime = Date.now();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify(requestBody)
    });

    const executionTime = Date.now() - startTime;
    console.log(`Bounds search response status: ${response.status}, execution time: ${executionTime}ms`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Bounds search API responded with status ${response.status}: ${errorText}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: BoundsSearchResponse = await response.json();
    
    console.log('Bounds search completed successfully:', {
      entitiesCount: data.entities?.length || 0,
      total: data.total,
      boundsEcho: data.bounds,
      executionTime: `${executionTime}ms`
    });

    return data;
  } catch (error) {
    console.error('Error in searchEntitiesByBounds:', error);
    
    if (error instanceof Error) {
      throw new Error(`Failed to search entities by bounds: ${error.message}`);
    }
    throw new Error('An unknown error occurred while searching entities by bounds');
  }
};

// Utility function to create debounced bounds search
export const createDebouncedBoundsSearch = (
  callback: (bounds: BoundsSearchResponse) => void,
  delay: number = 300
) => {
  let timeoutId: NodeJS.Timeout;
  
  return async (request: BoundsSearchRequest) => {
    // Clear previous timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    // Set new timeout
    timeoutId = setTimeout(async () => {
      try {
        const result = await searchEntitiesByBounds(request);
        callback(result);
      } catch (error) {
        console.error('Error in debounced bounds search:', error);
        // Call callback with error state
        callback({
          entities: [],
          total: 0,
          pages: 0,           // Add this
          current_page: 1,    // Add this
          bounds: request.bounds,
          zoom: request.zoom,
          error: error instanceof Error ? error.message : 'Unknown error occurred'
        });
      }
    }, delay);
  };
};

// Utility function to convert Google Maps bounds to our Bounds interface
export const googleMapsBoundsToLocalBounds = (
  googleBounds: google.maps.LatLngBounds
): Bounds => {
  const ne = googleBounds.getNorthEast();
  const sw = googleBounds.getSouthWest();
  
  return {
    north: ne.lat(),
    south: sw.lat(),
    east: ne.lng(),
    west: sw.lng()
  };
};
